import React, { Suspense } from 'react'
import screenLoader from 'utils/screenLoader'
import { Switch, Route, Redirect, withRouter } from 'react-router-dom'
import Login from 'screens/login'
import Logout from 'screens/logout'
import EmailAnswer from 'screens/login/email-answer'
import MyAccess from 'screens/site/my-access'
import LanguageSwitcher from 'screens/site/language-switcher'
import PrivateRoute from 'routes/site/PrivateRoute'
import PrivateAdminRoute from 'routes/admin/PrivateRoute'
import ClientManagementPrivateRoute from 'routes/client-management/PrivateRoute'
import {
  siteScreenLoader as SiteScreenLoader,
  adminScreenLoader as AdminScreenLoader,
  clientManagementScreenLoader as ClientManagementScreenLoader
} from 'routes/loaders'
import LoginByJWT from 'screens/login-by-jwt'
import { NotFound } from 'screens/http-status'
import {
  SchoolNotFound,
  SchoolConfirmationLogin,
  SchoolAccessRetrieval
} from 'screens/public/family-paying-access'
import OngoingBooksSubtitle from 'screens/site/my-readings/ongoing-books/ongoing-books-subtitle'
import ConnectionStatus from 'components/connection-status'
import * as paths from './paths'
import { getCookie, setCookie } from 'utils/cookies'
import { isIOS, isAndroid } from 'react-device-detect'
import RecoverPassword from 'screens/login/recover-password'
import RecoverEmailSent from 'screens/login/recover-email-sent'
import HomeStoryTree from 'screens/site/home/story-tree/HomeStoryTree'
import TabIngles from 'screens/site/home/tab-ingles'
import { Bookfair, BookfairSuccess } from 'screens/site/bookfair'
import { useNativeNavigate } from 'utils/hooks/use-native-navigate'
import { FAMILY_INVITE_TITLE } from 'features/family-invite/familyInvite.constants'

const TrackerPageview = props => {
  global.ga('send', {
    hitType: 'pageview',
    page: props.location.pathname
  })

  return null
}

//This change was made so that HomeModular can keep the cache between nested routes
const HomeModular = SiteScreenLoader('Home')
const EntityUsersManagementV2 = AdminScreenLoader('EntityUsersManagementV2')
const MyReadingsModular = SiteScreenLoader('MyReadings')
const JourneyModular = SiteScreenLoader('Journey')
const ReadingModular = SiteScreenLoader('OngoingBooks')
const ReadModular = SiteScreenLoader('FinishedBooks')
const IndicatedModular = SiteScreenLoader('IndicatedBooks')
const FavoritesModular = SiteScreenLoader('FavoritesBooks')
const IntermediateModular = SiteScreenLoader('Intermediate')
const AdminShowActiviy = AdminScreenLoader('ShowActivity')
const AdminShowActivityV2 = AdminScreenLoader('ShowActivityV2')
const ExploreModular = SiteScreenLoader('Explore')
const ProfileMenuModular = SiteScreenLoader('ProfileMenu')
const Payment = SiteScreenLoader('Payment')
const PendingPayment = SiteScreenLoader('PendingPayment')
const ManageFeatures = AdminScreenLoader('ManageFeatures')
const Newsstand = SiteScreenLoader('Newsstand')
const ActivityView = React.lazy(
  () => import('screens/admin-v2/activities-v2/ActivityView')
)
const FamilyInviteChoose = React.lazy(
  () => import('features/family-invite/FamilyInviteChoose')
)
const FamilyInvitePassPhone = React.lazy(
  () => import('features/family-invite/FamilyInvitePassPhone')
)

const Routes = () => {
  useNativeNavigate()

  const shouldTryToOpenApp = getCookie('open_app') === 'true'

  if (shouldTryToOpenApp) {
    const token = getCookie('access_token')

    if (token) {
      setCookie('open_app', 'false')
      window.location.href = `app.arvore://auth?token=${token}`
    }

    const androidAppStoreLink =
      'https://play.google.com/store/apps/details?id=arvoredelivros.com.br.arvore&hl=pt_br'
    const iosAppStoreLink =
      'itms-apps://itunes.apple.com/app/Árvore/id1094879125'

    setTimeout(() => {
      if (isIOS) {
        window.location.href = iosAppStoreLink
      }
      if (isAndroid) {
        window.location.href = androidAppStoreLink
      }
    }, 1000)
  }

  return (
    <Suspense fallback={null}>
      <ConnectionStatus />
      <Route path="*">{TrackerPageview}</Route>

      <Switch>
        {/* Site routes */}
        <Route
          exact
          path={[
            paths.ROOT_PATH,
            paths.LIVROS_HOME_ROOT_PATH,
            paths.LER_HOME_ROOT_PATH,
            paths.LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
            paths.LER_NOTICIAS_HOME_ROOT_PATH,
            paths.LER_VIDEOS_HOME_ROOT_PATH,
            paths.LER_PODCASTS_HOME_ROOT_PATH,
            paths.LER_TRILHAS_HOME_ROOT_PATH,
            paths.INGLES_HOME_ROOT_PATH,
            paths.COMUNICAR_HOME_ROOT_PATH,
            paths.ATUALIDADES_HOME_ROOT_PATH,
            paths.SENTIR_HOME_ROOT_PATH,
            paths.SENTIR_DIARIO_HOME_ROOT_PATH,
            paths.SENTIR_EMOTION_JOURNAL
          ]}
        >
          <PrivateRoute component={HomeModular} />
        </Route>

        <Route exact path={paths.COMUNICAR_STORY_TREE_HOME_ROOT_PATH}>
          <PrivateRoute component={TabIngles} noPaddingForAnyScreen />
        </Route>

        <Route exact path={paths.STORY_TREE_PATH}>
          <PrivateRoute component={HomeStoryTree} />
        </Route>

        <Route exact path={paths.PAYMENT_PATH}>
          <PrivateRoute readingLayout component={Payment} />
        </Route>

        <Route exact path={paths.PENDING_PAYMENT_PATH}>
          <PrivateRoute readingLayout component={PendingPayment} />
        </Route>

        <Route exact path={paths.BOOKFAIR_PATH}>
          <PrivateRoute readingLayout component={Bookfair} />
        </Route>

        <Route
          exact
          path={paths.BOOKFAIR_SUCCESS_PATH}
          component={BookfairSuccess}
        />

        <Route exact path={paths.TRIAL_START_PATH}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('TrialStart')}
          />
        </Route>

        <Route exact path={paths.TRIAL_END_PATH}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('TrialEnd')}
          />
        </Route>

        <Route
          exact
          path={paths.TRIAL_SELF_REGISTER_PATH}
          component={screenLoader(
            () => import('screens/public/trial'),
            'TrialSelfRegister'
          )}
        />

        <Route
          exact
          path={paths.TRIAL_SELF_REGISTER_WITH_CODE_PATH()}
          component={screenLoader(
            () => import('screens/public/trial'),
            'TrialSelfRegister'
          )}
        />

        <Route
          exact
          path={paths.TRIAL_SUCCESS_SELF_REGISTER_PATH()}
          component={screenLoader(
            () => import('screens/public/trial'),
            'TrialSelfRegisterSuccess'
          )}
        />

        <Route
          exact
          path={paths.TRIAL_VOUCHER_SELF_REGISTER_WITH_CODE_PATH()}
          component={screenLoader(
            () => import('screens/public/trial'),
            'TrialSelfRegister'
          )}
        />

        <Route
          exact
          path={paths.TRIAL_VOUCHER_SUCCESS_SELF_REGISTER_PATH()}
          component={screenLoader(
            () => import('screens/public/trial'),
            'TrialSelfRegisterSuccess'
          )}
        />

        <Route exact path={paths.PADLET_PATH}>
          <PrivateRoute
            noPaddingForAnyScreen
            noWidth
            component={SiteScreenLoader('Padlet')}
          />
        </Route>

        <Route exact path={paths.CATEGORY_PATH()}>
          <PrivateRoute
            title={'Explorar'}
            dontShowDesktopTitle
            component={withRouter(({ match }) => {
              const categorySeached = match?.params?.name

              return (
                <Redirect
                  to={`${paths.SEARCH_PATH_ROOT}?custom_categories=${categorySeached}`}
                />
              )
            })}
          />
        </Route>

        <Route exact path={paths.INDICATED_PATH}>
          <PrivateRoute component={SiteScreenLoader('Indicated')} />
        </Route>

        <Route exact path={paths.COLLECTION_PATH()}>
          <PrivateRoute
            noPaddingForAnyScreen
            noWidth
            component={SiteScreenLoader('Collection')}
          />
        </Route>

        <Route exact path={paths.PUBLISHER_PATH()}>
          <PrivateRoute
            noPaddingForAnyScreen
            noWidth
            component={SiteScreenLoader('Publisher')}
          />
        </Route>

        <Route exact path={paths.AUTHOR_PATH()}>
          <PrivateRoute
            noPaddingForAnyScreen
            noWidth
            component={SiteScreenLoader('Author')}
          />
        </Route>

        <Route exact path={paths.SEARCH_PATH_OLD_TEMP()}>
          <PrivateRoute
            title={'Busca'}
            dontShowDesktopTitle
            component={SiteScreenLoader('SearchResults')}
          />
        </Route>

        <Route exact path={paths.LEAGUE_PATH}>
          <PrivateRoute
            contentClassName="league-content"
            noPaddingForMobileScreen
            component={SiteScreenLoader('League')}
          />
        </Route>

        <Route exact path={paths.FOREST_PATH}>
          <PrivateRoute
            forestLayout={true}
            title={'Floresta'}
            dontShowDesktopTitle
            contentClassName="forest-content"
            component={SiteScreenLoader('HomeForest')}
          />
        </Route>

        <Route exact path={paths.FOREST_TREES_PATH}>
          <PrivateRoute
            forestLayout={true}
            title={'Árvores'}
            dontShowDesktopTitle
            contentClassName="forest-trees-content"
            component={SiteScreenLoader('Forest')}
          />
        </Route>

        <Route exact path={paths.LEAGUE_TUTORIAL_PATH}>
          <PrivateRoute
            contentClassName="league-content"
            component={SiteScreenLoader('LeagueTutorial')}
          />
        </Route>

        <Route exact path={paths.MY_ACCESS_PATH}>
          <PrivateRoute
            title={'Meu Acesso'}
            dontShowDesktopTitle
            component={MyAccess}
          />
        </Route>

        <Route exact path={paths.MY_NOTIFICATION_PREFERENCES_PATH}>
          <PrivateRoute
            title={'Preferências de Notificação'}
            dontShowDesktopTitle
            component={MyAccess}
          />
        </Route>

        <Route exact path={paths.MY_ACCOUNT_PATH}>
          <PrivateRoute
            title={'Minha Conta'}
            dontShowDesktopTitle
            component={MyAccess}
          />
        </Route>

        <Route exact path={paths.MY_BOOKS_PATH}>
          <PrivateRoute
            title={'Meus Livros'}
            dontShowDesktopTitle
            component={MyAccess}
          />
        </Route>

        <Route exact path={paths.LANGUAGES_SWITCHER_PATH}>
          <PrivateRoute
            component={LanguageSwitcher}
            title={'Idioma'}
            dontShowDesktopTitle
          />
        </Route>

        <Route exact path={paths.USER_PROFILE_MENU}>
          <PrivateRoute
            title={'Configurações'}
            dontShowDesktopTitle
            component={ProfileMenuModular}
          />
        </Route>

        <Route exact path={paths.RESET_PASSWORD_PATH}>
          <PrivateRoute component={SiteScreenLoader('ResetPassword')} />
        </Route>

        <Route exact path={paths.FIRST_RESET_PASSWORD_PATH}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('ResetPassword')}
          />
        </Route>

        <Route exact path={paths.ACTIVITIES_DO_ACTIVITY_PATH()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('DoActivity')}
          />
        </Route>

        <Route exact path={paths.ACTIVITIES_DO_ACTIVITY_PATH_V2()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('DoActivityV2')}
          />
        </Route>

        <Route exact path={paths.ACTIVITIES_FINISHED_PATH()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('ActivityFinished')}
          />
        </Route>

        <Route exact path={paths.ACTIVITIES_SEE_CORRECTED_ACTIVITY_PATH()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('SeeCorrectedActivity')}
          />
        </Route>

        <Route exact path={paths.ACTIVITIES_PATH}>
          <PrivateRoute
            title={'Tarefas'}
            dontShowDesktopTitle
            component={SiteScreenLoader('Activities')}
          />
        </Route>

        <Route exact path={paths.EXPLORE_PATH}>
          <PrivateRoute
            dontShowDesktopTitle
            noWidth
            noPaddingForAnyScreen
            component={ExploreModular}
            title={'Explorar'}
          />
        </Route>

        <Route exact path={paths.LER_NEWSSTAND_ROOT_PATH}>
          <PrivateRoute
            dontShowDesktopTitle
            noWidth
            noPaddingForAnyScreen
            component={Newsstand}
            title={'Revistas e Jornais'}
          />
        </Route>

        <Route exact path={paths.JOURNEY_HOME_ROOT_PATH}>
          <PrivateRoute
            dontShowDesktopTitle
            noPaddingForAnyScreen
            noPaddingForMobileScreen
            noWidth
            component={JourneyModular}
          />
        </Route>

        <Route exact path={paths.FAMILY_INVITE_PATH}>
          <PrivateRoute
            pathGoBack={paths.ROOT_PATH}
            title={FAMILY_INVITE_TITLE}
            component={FamilyInviteChoose}
          />
        </Route>

        <Route exact path={paths.FAMILY_INVITE_PASS_PHONE_PATH}>
          <PrivateRoute
            pathGoBack={paths.FAMILY_INVITE_PATH}
            title={FAMILY_INVITE_TITLE}
            component={FamilyInvitePassPhone}
          />
        </Route>

        <Route exact path={paths.MY_READINGS_MODULAR_PATH}>
          <PrivateRoute
            title={'Minhas leituras'}
            dontShowDesktopTitle
            component={MyReadingsModular}
          />
        </Route>

        <Route exact path={paths.MY_READINGS_MODULAR_READING_PATH}>
          <PrivateRoute
            pathGoBack={paths.APP_V2_MY_READINGS_PATH}
            title={'Lendo'}
            subtitle={OngoingBooksSubtitle}
            component={ReadingModular}
            noPaddingForMobileScreen
            noPaddingForAnyScreen
          />
        </Route>

        <Route exact path={paths.MY_READINGS_MODULAR_READ_PATH}>
          <PrivateRoute
            pathGoBack={paths.APP_V2_MY_READINGS_PATH}
            title={'Lidos'}
            component={ReadModular}
            noPaddingForMobileScreen
            noPaddingForAnyScreen
          />
        </Route>

        <Route exact path={paths.MY_READINGS_MODULAR_INDICATED_PATH}>
          <PrivateRoute
            pathGoBack={paths.APP_V2_MY_READINGS_PATH}
            title={'Indicados'}
            component={IndicatedModular}
            noPaddingForMobileScreen
            noPaddingForAnyScreen
          />
        </Route>

        <Route exact path={paths.MY_READINGS_MODULAR_FAVORITES_PATH}>
          <PrivateRoute
            pathGoBack={paths.APP_V2_MY_READINGS_PATH}
            title={'Favoritos'}
            component={FavoritesModular}
            noPaddingForMobileScreen
            noPaddingForAnyScreen
          />
        </Route>

        <Route exact path={paths.SHOW_OLYMPICS_SHELF}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('OlympicsShelfPage')}
          />
        </Route>

        <Route exact path={paths.SHOW_EVENT_SHELF()}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('OlympicsShelfPage')}
          />
        </Route>

        <Route exact path={paths.SHOW_BIBLION_SHELF()}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('BiblionShelfPage')}
          />
        </Route>

        <Route exact path={paths.SHOW_SHELF()}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('ShowShelf')}
          />
        </Route>

        <Route exact path={paths.SEARCH_PATH()}>
          <PrivateRoute
            title={'Busca'}
            dontShowDesktopTitle
            component={SiteScreenLoader('SearchResults')}
          />
        </Route>

        <Route exact path={paths.LEVEL_SEARCH_PAGE()}>
          <PrivateRoute
            title={'Busca'}
            dontShowDesktopTitle
            component={SiteScreenLoader('SearchResults')}
          />
        </Route>

        <Route
          exact
          path={[
            paths.DEPRECATED_LIVRO_MODULE_BASED_INTERMEDIATE_PATH(),
            paths.INTERMEDIATE_DETAILS_BOOKS_PATH()
          ]}
        >
          <PrivateRoute
            component={withRouter(({ match }) => {
              return (
                <Redirect
                  to={paths.INTERMEDIATE_DETAILS_BOOK_PATH(match?.params?.slug)}
                />
              )
            })}
          />
        </Route>

        <Route
          exact
          path={[
            paths.DEPRECATED_ATUALIDADES_INTERMEDIATE_PATH(),
            paths.INTERMEDIATE_DETAILS_ARTICLES_PATH()
          ]}
        >
          <PrivateRoute
            component={withRouter(({ match }) => {
              return (
                <Redirect
                  to={paths.INTERMEDIATE_DETAILS_ARTICLE_PATH(
                    match?.params?.id
                  )}
                />
              )
            })}
          />
        </Route>

        <Route exact path={paths.DEPRECATED_SHOW_SHELF()}>
          <PrivateRoute
            component={withRouter(({ match }) => {
              return <Redirect to={paths.SHOW_SHELF(match?.params?.id)} />
            })}
          />
        </Route>

        <Route exact path={paths.DEPRECATED_SHOW_ATUALIDADES_SHELF()}>
          <PrivateRoute
            component={withRouter(({ match }) => {
              return (
                <Redirect
                  to={paths.SHOW_ATUALIDADES_SHELF(match?.params?.name)}
                />
              )
            })}
          />
        </Route>

        <Route exact path={paths.DEPRECATED_SHOW_SENTIR_SHELF()}>
          <PrivateRoute
            component={withRouter(({ match }) => {
              return (
                <Redirect to={paths.SHOW_SENTIR_SHELF(match?.params?.id)} />
              )
            })}
          />
        </Route>

        <Route exact path={paths.INTERMEDIATE_DETAILS_BOOK_PATH()}>
          <PrivateRoute component={IntermediateModular} />
        </Route>

        <Route exact path={paths.SHOW_ATUALIDADES_SHELF()}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('ShowShelf')}
          />
        </Route>

        <Route exact path={paths.INTERMEDIATE_DETAILS_ARTICLE_PATH()}>
          <PrivateRoute component={IntermediateModular} />
        </Route>

        <Route
          exact
          path={paths.DEPRECATED_ATUALIDADES_INTERMEDIATE_DETAILS_PATH_ARTICLE_READER()}
        >
          <PrivateRoute
            component={withRouter(({ match }) => {
              return (
                <Redirect
                  to={paths.INTERMEDIATE_DETAILS_PATH_ARTICLE_READER(
                    match?.params?.id
                  )}
                />
              )
            })}
          />
        </Route>

        <Route exact path={paths.INTERMEDIATE_DETAILS_PATH_ARTICLE_READER()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('AtualidadesReader')}
          />
        </Route>

        <Route exact path={paths.INTERMEDIATE_DETAILS_BOOK_PATH()}>
          <PrivateRoute component={IntermediateModular} />
        </Route>

        <Route exact path={paths.SHOW_SENTIR_SHELF()}>
          <PrivateRoute
            noPaddingForAnyScreen
            component={SiteScreenLoader('ShowShelf')}
          />
        </Route>

        <Route exact path={paths.COLLECTION_SENTIR_PATH()}>
          <PrivateRoute component={SiteScreenLoader('CollectionSentir')} />
        </Route>

        <Route exact path={paths.READING_FLUENCY}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('ReadingFluency')}
          />
        </Route>

        <Route exact path={paths.JOURNEY_EVALUATION}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('JourneyEvaluation')}
          />
        </Route>

        <Route exact path={paths.READER_RATING()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('ReaderBoard')}
          />
        </Route>

        <Route exact path={paths.RECORDING_AUDIO()}>
          <PrivateRoute
            readingLayout
            component={SiteScreenLoader('RecordingAStory')}
          />
        </Route>

        {/* Admin routes */}
        <Route exact path={paths.ADMIN_ROOT_PATH}>
          <PrivateAdminRoute component={AdminScreenLoader('Home')} />
        </Route>

        <Route exact path={paths.ADMIN_SUMMARY_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('HomeAdminSummary')}
          />
        </Route>

        <Route exact path={paths.ADMIN_SUMMARY_PATH_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('HomeAdminSummaryV2')}
          />
        </Route>

        <Route exact path={paths.ADMIN_READING_REPORT_PATH}>
          <PrivateAdminRoute
            withoutLayout
            isResponsive
            component={AdminScreenLoader('ReadingReport')}
          />
        </Route>

        <Route exact path={paths.ADMIN_MANAGE_FEATURES}>
          <PrivateAdminRoute component={ManageFeatures} />
        </Route>

        <Route exact path={paths.ADMIN_PUBLIC_ACTIVITIES_PATH_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('PublicActivities')}
          />
        </Route>

        <Route exact path={paths.ADMIN_TIME_USAGE_REPORTS_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('HomeTimeUsageReports')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACCESS_REPORT_PATH}>
          <PrivateAdminRoute component={AdminScreenLoader('AccessReport')} />
        </Route>

        <Route exact path={paths.ADMIN_READING_GOALS_REPORT_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ReadingGoalsReport')}
          />
        </Route>

        <Route exact path={paths.ADMIN_PAYMENTS_REPORT_PATH}>
          <PrivateAdminRoute component={AdminScreenLoader('PaymentsReport')} />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_DIDACTIC_SEQUENCE_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('BnccDidacticSequenceViewer')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_ALL_DIDACTIC_SEQUENCE_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('BnccAllDidacticSequence')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_MY_DIDACTIC_SEQUENCE_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('BnccMyDidacticSequence')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_DIDACTIC_SEQUENCE_INFO_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('BnccInfo')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_DIDACTIC_SEQUENCE_DETAILS_PATH()}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('BnccDidacticSequenceDetails')}
          />
        </Route>

        <Route exact path={paths.ADMIN_REPORTS_PATH}>
          <PrivateAdminRoute component={AdminScreenLoader('Reports')} />
        </Route>

        <Route exact path={paths.ADMIN_TASK_DETAILS_PATH()}>
          <PrivateAdminRoute component={AdminScreenLoader('TaskDetails')} />
        </Route>

        <Route exact path={paths.READING_REPORTS_V2_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ReadingReportsV2')}
          />
        </Route>

        <Route exact path={paths.READING_REPORTS_V2_USER_VIEW_PATH()}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ReadingReportsV2UserView')}
          />
        </Route>

        <Route exact path={paths.ADMIN_CLASS_REPORTS_PATH()}>
          <PrivateAdminRoute component={AdminScreenLoader('Reports')} />
        </Route>

        <Route exact path={paths.ADMIN_STUDENT_REPORTS_PATH()}>
          <PrivateAdminRoute component={AdminScreenLoader('StudentReports')} />
        </Route>

        <Route exact path={paths.ADMIN_APPLIED_ACTIVITY_USERS_REPORT()}>
          <PrivateAdminRoute
            component={AdminScreenLoader('AppliedActivityUsersReports')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BOOK_REPORTS_PATH()}>
          <PrivateAdminRoute component={AdminScreenLoader('BookReports')} />
        </Route>

        <Route exact path={paths.ADMIN_ARTICLE_REPORTS_PATH()}>
          <PrivateAdminRoute component={AdminScreenLoader('ArticleReports')} />
        </Route>

        <Route exact path={paths.ADMIN_BOOK_INDICATION_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ContentIndication')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BOOKS_MANAGEMENT_PATH}>
          <PrivateAdminRoute component={AdminScreenLoader('BooksManagement')} />
        </Route>

        <Route exact path={paths.ADMIN_MANAGE_COLLECTION_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ManageCollection')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ENTITY_USERS_PATH}>
          <Redirect to={paths.ADMIN_ENTITY_USERS_V2_PATH} />
        </Route>

        <Route exact path={paths.ADMIN_ENTITY_USERS_V2_PATH}>
          <PrivateAdminRoute component={EntityUsersManagementV2} />
        </Route>

        <Route exact path={paths.ADMIN_ENTITY_USERS_ONLINE_STATUS_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('EntityUsersOnlineStatus')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ENTITY_USERS_ONLINE_STATUS_PATH_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('EntityUsersOnlineStatusV2')}
          />
        </Route>

        <Route exact path={paths.ADMIN_USER_CARD_ID()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('UserCardID')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ENTITY_CARD_ID()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('UserCardID')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ROOT_CARD_ID()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('UserCardID')}
          />
        </Route>

        <Route exact path={paths.ADMIN_API_LIST}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={AdminScreenLoader('APIList')}
          />
        </Route>

        <Route exact path={paths.ADMIN_API_FORM()}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={AdminScreenLoader('APIForm')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ALL_BEST_READERS_CLASSES}>
          <PrivateAdminRoute
            component={AdminScreenLoader('AllBestReadersClasses')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ALL_BEST_READERS}>
          <PrivateAdminRoute
            component={AdminScreenLoader('AllBestReadersClasses')}
          />
        </Route>

        <Route exact path={paths.ADMIN_LEAGUE_REPORT}>
          <PrivateAdminRoute component={AdminScreenLoader('LeagueReport')} />
        </Route>

        <Route exact path={paths.ADMIN_LEAGUE_REPORT_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('LeagueReadersReport')}
          />
        </Route>

        <Route exact path={paths.ADMIN_JOB_POSITIONS_AND_PERMISSIONS}>
          <PrivateAdminRoute
            component={AdminScreenLoader('JobPositionsAndPermissions')}
          />
        </Route>

        <Route exact path={paths.ADMIN_SHELF_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ShelfManagement')}
          />
        </Route>

        <Route exact path={paths.BOOST_FOREST_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('BoostForest')}
          />
        </Route>

        <Route exact path={paths.MOVE_TO_ROOT_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('MoveToRoot')}
          />
        </Route>

        <Route exact path={paths.ADMIN_SHELF_MANAGEMENT_CREATE}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ShelfManagementForm')}
          />
        </Route>

        <Route exact path={paths.ADMIN_SHELF_MANAGEMENT_EDIT()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ShelfManagementForm')}
          />
        </Route>

        <Route exact path={paths.ADMIN_SELECT_ENTITY_PATH}>
          <PrivateAdminRoute
            isResponsive
            withoutLayout
            component={AdminScreenLoader('SelectEntity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_MANAGE_DIDACTIC_SEQUENCES_PATH}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ManageDidacticSequences')}
          />
        </Route>

        <Route exact path={paths.ADMIN_BNCC_MANAGE_DIDACTIC_SEQUENCE_PATH()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('CreateDidacticSequence')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_HOME_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('ActivitiesHome')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_HOME_PATH_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesHomeV2')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_MY_ACTIVITIES_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesMyActivities')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_APPLIED_ACTIVITIES_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesAppliedActivities')}
          />
        </Route>

        <Route exact path={paths.ADMIN_PUBLIC_ACTIVITIES_PATH_V1}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('ActivitiesPublicActivities')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_APPLIED_ACTIVITY_PATH()}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesAppliedActivity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_APPLIED_PER_CLASS_PATH()}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesAppliedPerClass')}
          />
        </Route>

        <Route
          exact
          path={paths.ADMIN_ACTIVITIES_DETAIL_APPLIED_ACTIVITY_PATH()}
        >
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesDetailAppliedActivity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_CREATE_ACTIVITY_PATH}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('ActivitiesCreateActivity')}
          />
        </Route>

        <Route
          exact
          path={paths.ADMIN_ACTIVITIES_CREATE_ACTIVITY_SIMPLE_PATH()}
        >
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('CreateActivitySimple')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_SHOW_ACTIVITY_PATH()}>
          <PrivateAdminRoute withoutLayout component={AdminShowActiviy} />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_SHOW_ACTIVITY_PATH_V2()}>
          <PrivateAdminRoute withoutLayout component={AdminShowActivityV2} />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_VIEW_ACTIVITY_PATH_V2()}>
          <PrivateAdminRoute component={ActivityView} />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_SHOW_ACTIVITY_SIMPLE_PATH()}>
          <PrivateAdminRoute withoutLayout component={AdminShowActiviy} />
        </Route>

        <Route
          exact
          path={paths.ADMIN_ACTIVITIES_SHOW_ACTIVITY_SIMPLE_PATH_V2()}
        >
          <PrivateAdminRoute withoutLayout component={AdminShowActivityV2} />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_EDIT_ACTIVITY_PATH()}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('ActivitiesEditActivity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_SIMPLE_EDIT_ACTIVITY_PATH()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivitiesEditActivity', {
              isSimpleLayout: true
            })}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_APPLY_ACTIVITY_PATH()}>
          <PrivateAdminRoute
            sidebarMinimized={true}
            component={AdminScreenLoader('ActivitiesApplyActivity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_APPLY_ACTIVITY_SIMPLE_PATH()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivitiesApplyActivity', {
              isSimpleLayout: true
            })}
          />
        </Route>

        <Route
          exact
          path={paths.ADMIN_ACTIVITIES_REVIEW_ACTIVITY_TEMPLATE_PATH()}
        >
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivitiesReviewActivityModel')}
          />
        </Route>

        <Route
          exact
          path={paths.ADMIN_ACTIVITIES_SIMPLE_REVIEW_ACTIVITY_TEMPLATE_PATH()}
        >
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivitiesReviewActivityModel')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_CORRECT_ACTIVITY_PATH()}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivitiesCorrectActivity')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_CORRECT_ACTIVITY_PATH_V2()}>
          <PrivateAdminRoute withoutLayout component={AdminShowActivityV2} />
        </Route>

        <Route exact path={paths.ACTIVITIES_CORRECT_ACTIVITY_PATH_V2()}>
          <PrivateRoute component={AdminShowActivityV2} />
        </Route>

        <Route exact path={paths.ADMIN_PROJECTS_WELCOME}>
          <PrivateAdminRoute component={AdminScreenLoader('Welcome')} />
        </Route>

        <Route exact path={paths.ADMIN_READING_ASSESSMENT_HOME_V2}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ReadingAssessmentV2')}
          />
        </Route>

        <Route exact path={paths.ADMIN_RUNNING_RECORDS_REPORT_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('RunningRecordsReport')}
          />
        </Route>

        <Route exact path={paths.ADMIN_RUNNING_RECORDS_APPLY_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('RunningRecordsApply')}
          />
        </Route>

        <Route exact path={paths.ADMIN_ACTIVITIES_REPORT_PATH}>
          <PrivateAdminRoute
            component={AdminScreenLoader('ActivitiesReport')}
          />
        </Route>

        {/** Admin Mobile Routes */}
        <Route exact path={paths.ADMIN_ROOT_PATH_MOBILE}>
          <PrivateRoute component={AdminScreenLoader('HomeMobile')} />
        </Route>

        <Route exact path={paths.ACTIVITY_REPORT_PATH_MOBILE}>
          <PrivateRoute component={AdminScreenLoader('ActivityReport')} />
        </Route>

        <Route exact path={paths.TASK_REPORT_PATH_MOBILE}>
          <PrivateRoute component={AdminScreenLoader('TaskReport')} />
        </Route>

        <Route exact path={paths.APPLY_ACTIVITIES_PATH_MOBILE}>
          <PrivateRoute
            component={AdminScreenLoader('ApplyActivity')}
            title="Minhas atividades"
          />
        </Route>

        <Route exact path={paths.APPLY_ACTIVITY_PATH_MOBILE()}>
          <PrivateRoute
            component={AdminScreenLoader('ActivityPage')}
            title="Detalhes da atividade"
          />
        </Route>

        <Route exact path={paths.APPLY_ACTIVITY_FLOW_PATH_MOBILE()}>
          <PrivateRoute
            component={AdminScreenLoader('ApplyActivityFlow')}
            title="Detalhes da atividade"
          />
        </Route>

        <Route exact path={paths.ENTITIES_AND_USERS_PATH_MOBILE}>
          <PrivateRoute component={AdminScreenLoader('EntitiesAndUsers')} />
        </Route>

        <Route
          exact
          path={paths.ADMIN_MOBILE_ACTIVITIES_SHOW_ACTIVITY_PATH_V2()}
        >
          <PrivateRoute component={AdminScreenLoader('AdminShowActivityV2')} />
        </Route>
        {/** End Admin Mobile Routes */}

        {/* Anyone can access */}
        <Route
          path={paths.RECOVER_ACCESS_CONFIRM_SENT_PATH}
          component={RecoverEmailSent}
        />
        <Route path={paths.RECOVER_ACCESS_PATH} component={EmailAnswer} />
        <Route path={paths.RECOVER_PASSWORD_PATH} component={RecoverPassword} />
        <Route path={paths.LOGIN_PATH} component={Login} />
        <Route path={paths.LOGOUT_PATH} component={Logout} />
        <Route path={paths.LOGIN_BY_JWT_PATH} exact component={LoginByJWT} />
        <Route path={paths.LANDING_COMBO_PATH} exact component={LoginByJWT} />
        <Route
          path={paths.LANDING_FREEMIUM_PATH}
          exact
          component={LoginByJWT}
        />
        <Route path={'/auth/sso'} component={LoginByJWT} />

        <Route exact path={paths.SLIDER_ADMIN}>
          <PrivateAdminRoute
            withoutLayout={true}
            component={AdminScreenLoader('SliderAdmin')}
          />
        </Route>

        {/** Running Records */}
        <Route exact path={paths.RUNNING_RECORDS_PATH}>
          <PrivateRoute
            noPaddingForAnyScreen
            noWidth
            component={SiteScreenLoader('RunningRecords')}
          />
        </Route>

        {/* Client Management */}
        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENTS}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Clients')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_USERS}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Users')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_USER_PAGE()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('User')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENT_PAGE()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Client')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENT_IMPORT_REGISTER()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('ClientImport')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENT_IMPORT_REGROUP()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('ClientImport')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENT_IMPORT_MIGRATE()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('ClientImport')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CLIENT_CARD_ID()}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={ClientManagementScreenLoader('UserCardID')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CONTRACTS}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Contracts')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CONTRACTS_PAGE()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Contract')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_PROCESS}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('ProcessList')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_REVIEW_PROCESS()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('ProcessReview')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CANCELED_PROCESS()}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('CanceledProcess')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CONTENT}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Contents')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CONTENT_PAGE()}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={ClientManagementScreenLoader('ContentDetails')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_ACTIVITIES}>
          <ClientManagementPrivateRoute
            component={ClientManagementScreenLoader('Activities')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_EDIT_ACTIVITIES()}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={ClientManagementScreenLoader('CreateActivity')}
          />
        </Route>

        <Route exact path={paths.CLIENT_MANAGEMENT_CREATE_ACTIVITIES}>
          <ClientManagementPrivateRoute
            withoutLayout
            component={ClientManagementScreenLoader('CreateActivity')}
          />
        </Route>

        <Route
          exact
          path={paths.BOOKFAIR_SCHOOL_CONFIRMATION_LOGIN()}
          component={SchoolConfirmationLogin}
        />

        <Route exact path={paths.EDIT_MANY_USERS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('EditManyUsers')}
          />
        </Route>

        <Route exact path={paths.INSERT_BOOKS_TO_COLLECTIONS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('BooksToCollection')}
          />
        </Route>

        <Route exact path={paths.ACTIVITY_BOOKS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ActivityBooks')}
          />
        </Route>

        <Route exact path={paths.BOOK_QUESTIONS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('BookQuestions')}
          />
        </Route>

        <Route exact path={paths.BOOK_PROCESSING_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('BookProcessing')}
          />
        </Route>

        <Route exact path={paths.DEACTIVATE_MANY_CLIENTS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('DeactivateManyClients')}
          />
        </Route>

        <Route exact path={paths.BULK_READING_FLUENCY}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('BulkReadingFluency')}
          />
        </Route>

        <Route exact path={paths.IMPORT_BOOKS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ImportBooks')}
          />
        </Route>

        <Route exact path={paths.LIST_PUBLISHERS_MANAGEMENT}>
          <PrivateAdminRoute
            withoutLayout
            component={AdminScreenLoader('ListPublishers')}
          />
        </Route>

        <Route exact path={paths.SCHOOL_NOT_FOUND} component={SchoolNotFound} />

        <Route
          exact
          path={paths.BOOKFAIR_SCHOOL_ACCESS_RETRIEVAL()}
          component={SchoolAccessRetrieval}
        />

        <Route path="*" component={NotFound} />
      </Switch>
    </Suspense>
  )
}

export default Routes
